/**
 * GlobalView — Keyboard Shortcuts Service (§2.4)
 *
 * Binds global keyboard shortcuts for navigation and interaction.
 * Call `installKeyboardShortcuts(viewer)` and store the cleanup fn.
 */

import * as Cesium from 'cesium';
import { useGlobeStore } from '../stores/globeStore';
import { stopTracking } from './aircraftTracker';
import { highlightAirEntity } from './layers/AirLayerRenderer';

interface ShortcutDef {
  key: string;
  description: string;
  handler: (viewer: Cesium.Viewer) => void;
}

const SHORTCUTS: ShortcutDef[] = [
  {
    key: 'r',
    description: 'Home (Zürich)',
    handler: () => {
      stopTracking();
      highlightAirEntity(null);
      useGlobeStore.getState().setSelectedEntity(null);
      useGlobeStore.getState().resetView();
    },
  },
  {
    key: 'e',
    description: 'Europe',
    handler: () => {
      stopTracking();
      highlightAirEntity(null);
      useGlobeStore.getState().setSelectedEntity(null);
      useGlobeStore.getState().flyTo(8.99723, 36.70628, 4_900_000, 2, 0, -76.1);
    },
  },
  {
    key: 'w',
    description: 'World',
    handler: () => {
      stopTracking();
      highlightAirEntity(null);
      useGlobeStore.getState().setSelectedEntity(null);
      useGlobeStore.getState().flyTo(8.54379, 47.36339, 20_000_000, 2.5, 0, -90);
    },
  },
  {
    key: 'Escape',
    description: 'Deselect',
    handler: () => {
      stopTracking();
      highlightAirEntity(null);
      useGlobeStore.getState().setSelectedEntity(null);
    },
  },
  {
    key: '+',
    description: 'Zoom',
    handler: (viewer) => {
      viewer.camera.zoomIn(viewer.camera.positionCartographic.height * 0.3);
    },
  },
  {
    key: '-',
    description: 'Zoom',
    handler: (viewer) => {
      viewer.camera.zoomOut(viewer.camera.positionCartographic.height * 0.3);
    },
  },
];

/**
 * Install keyboard shortcuts and return a cleanup function.
 */
export function installKeyboardShortcuts(viewer: Cesium.Viewer): () => void {
  const handleKeyDown = (e: KeyboardEvent) => {
    // Don't capture when typing in inputs
    const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

    if (viewer.isDestroyed()) return;

    for (const shortcut of SHORTCUTS) {
      if (e.key === shortcut.key) {
        e.preventDefault();
        shortcut.handler(viewer);
        return;
      }
    }
  };

  document.addEventListener('keydown', handleKeyDown);

  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
}

/** Get all registered shortcuts for displaying in help/HUD */
export function getShortcutList(): { key: string; description: string }[] {
  return SHORTCUTS.map((s) => ({ key: s.key, description: s.description }));
}
